import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  HostListener,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CmsserviceService } from '../../../common-cms-service/cmsservice.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

const PAGE_SIZE = 10;

@Component({
    selector: 'app-year-four-cal',
    templateUrl: './year-four-cal.component.html',
    styleUrls: ['./year-four-cal.component.css'],
    standalone: false
})
export class YearFourCalComponent implements OnInit, AfterViewInit, OnDestroy {

  // ─── NAV shadow (landing page) ───────────────────────────────────────
  isNavShadow = false;
  private revealObserver: any = null;

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.isNavShadow = window.scrollY > 20;
  }

  // ─── Landing page data (Tabler icon classes) ─────────────────────────
  heroPills = [
    { icon: 'ti-math-symbols', label: 'Maths',            sub: 'Problem-solving and arithmetic' },
    { icon: 'ti-book-2',       label: 'English',          sub: 'Comprehension and grammar' },
    { icon: 'ti-pencil',       label: 'Creative Writing', sub: 'Imagination and expression' },
    { icon: 'ti-puzzle',       label: 'VR and NVR',       sub: 'Reasoning and pattern recognition' },
  ];

  lpStats = [
    { number: '5',   suffix: '+', label: 'Subjects covered' },
    { number: '20',  suffix: '+', label: 'Weekly tests' },
    { number: '100', suffix: '%', label: 'Syllabus coverage' },
    { number: '4.9', suffix: '★', label: 'Parent rating' },
  ];

  lpSubjects = [
    { icon: 'ti-math-symbols', title: 'Maths',                desc: 'Problem-solving, arithmetic fluency and logical reasoning to build number confidence.' },
    { icon: 'ti-book-2',       title: 'English',              desc: 'Comprehension, grammar accuracy and vocabulary for well-rounded language skills.' },
    { icon: 'ti-pencil',       title: 'Creative Writing',     desc: 'Imagination, sentence structure and expression for stronger written work.' },
    { icon: 'ti-abc',          title: 'Verbal Reasoning',     desc: 'Early reasoning and language-based patterns to prepare for 11+ challenges.' },
    { icon: 'ti-shape',        title: 'Non-Verbal Reasoning', desc: 'Spatial reasoning and visual pattern practice to sharpen logical thinking.' },
  ];

  lpFeatures = [
    { icon: 'ti-calendar-week', title: 'Weekly tests',        desc: 'Topics covered in manageable weekly chunks, never overwhelming and always progressive.' },
    { icon: 'ti-clock-hour-4',  title: 'Flexible schedule',   desc: 'Balanced with school and home practice, running October to February at a comfortable pace.' },
    { icon: 'ti-chart-bar',     title: 'Consistent progress', desc: 'Steady growth without pressure, backed by a progress analysis report every week.' },
  ];

  lpBenefits = [
    { icon: 'ti-zoom-check',        title: 'Identify weak areas',        desc: 'Spot gaps early so your child can focus where it matters most.' },
    { icon: 'ti-report-analytics',  title: 'Progress analysis report',   desc: 'Track strengths and development over time with detailed weekly reports.' },
    { icon: 'ti-rocket',            title: 'Confidence boost',           desc: 'Step-by-step readiness builds real confidence for higher-level preparation.' },
    { icon: 'ti-list-check',        title: 'Complete syllabus coverage', desc: 'Maths, English, Creative Writing, VR and NVR, all covered in full.' },
  ];

  lpSteps = [
    { num: '1', title: 'Sign up',           desc: 'Quick online registration that takes less than 2 minutes.' },
    { num: '2', title: 'Choose your tests', desc: "Weekly tests from October to February, picked to fit your child's schedule." },
    { num: '3', title: 'Start practising',  desc: 'Instant access to structured topic tests and progress tracking.' },
  ];

  faqs = [
    { q: 'How do I book a Year 4 topic test?',     a: 'Pick a centre and date in the test calendar above, then tap Book seat.' },
    { q: 'Are these tests online or face to face?', a: 'Both. Use the Format filter to see only online or only face-to-face sessions.' },
    { q: 'Which exam boards do you cover?',         a: 'GL, CSSE, FSCE, SET and ISEB are all covered.' },
  ];

  // ─── Existing calendar component properties ──────────────────────────

  title = 'Year 4 Mock Test';
  slug: any;

  filters: any = { categoryId: 40, pattern: null, mode: null, month: null, center: null, search: '' };

  courses: any[] = [];
  categories: any[] = [];
  patternTypes: any[] = [];
  types: any[] = [];

  months = [
    { label: 'January',   value: 1  }, { label: 'February',  value: 2  }, { label: 'March',     value: 3  },
    { label: 'April',     value: 4  }, { label: 'May',       value: 5  }, { label: 'June',      value: 6  },
    { label: 'July',      value: 7  }, { label: 'August',    value: 8  }, { label: 'September', value: 9  },
    { label: 'October',   value: 10 }, { label: 'November',  value: 11 }, { label: 'December',  value: 12 },
  ];
  centers: string[] = [];

  testList: any[] = [];
  visibleCount = PAGE_SIZE;
  private allResults: any[] = [];

  ngUnsubscribe = new Subject<void>();
  catId = 0;
  catData: any;
  first = true;
  page = 0;
  locationName: any = '';

  private preselectCategoryId: number | null = null;
  private shouldAutoScroll = false;

  // caches so template helpers return stable values across change detection
  private codesCache: { [k: string]: string[] } = {};
  private datePartsCache: { [k: string]: any } = {};

  aviableCenters = [
    { name: 'Essex',           exams: 'CSSE & CCHS (FSCE)',    locations: 'Chelmsford | Southend | Basildon | Ilford', image: 'assets/images/essex-public-school.jpg' },
    { name: 'Kent',            exams: 'GL Kent & GL Bexley',   locations: 'Bexley Test Centres',                        image: 'assets/images/kent-school.png' },
    { name: 'Redbridge',       exams: 'GL Kent & GL Bexley',   locations: 'Ilford Centre',                              image: 'assets/images/redbridge-school.jpg' },
    { name: 'Buckinghamshire', exams: 'GL Kent & GL Bexley',   locations: 'Amersham | Milton Keynes Centres',           image: 'assets/images/buckshire-school.jpeg' },
    { name: 'Harrow',          exams: 'GL QE Boys, GL HBS Girls, GL Bucks', locations: 'Harrow Centre',               image: 'assets/images/harrow-school.jpg' },
  ];

  constructor(
    private router: ActivatedRoute,
    private titleService: Title,
    private categoryProduct: CmsserviceService,
    private route: Router,
    private ref: ChangeDetectorRef,
    private el: ElementRef
  ) {}

  // ─── Lifecycle ───────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadAssets();

    this.router.queryParams.subscribe(params => {
      if (params['category']) {
        const cid = Number(params['category']);
        if (!isNaN(cid)) {
          this.preselectCategoryId = 40;
          this.filters.categoryId = 40;
        }
      }
      if (params['scroll'] === 'calendar-section') {
        this.shouldAutoScroll = true;
      }
    });

    this.router.queryParams.subscribe(params => {
      this.locationName = params.location;
      const ul = $(".test-card-listing-body");
      const li = ul.find(".test-card");
      if (this.locationName && this.testList && this.testList.length) {
        this.locationSpecificList(this.locationName);
      } else if (!this.locationName && li && li.length) {
        this.locationSpecificList('');
      }
    });

    this.loadMasterFiltersAndRender();
    this.setupUiHandlers();
  }

  ngAfterViewInit(): void {
    this.initRevealObserver();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    if (this.revealObserver) {
      this.revealObserver.disconnect();
    }
  }

  // ─── Fonts + icons (no @import in component CSS) ─────────────────────

  private loadAssets(): void {
    this.addStylesheet('y4c-fonts',
      'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Nunito:wght@400;600;700;800&display=swap');
    this.addStylesheet('y4c-tabler',
      'https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@2.47.0/tabler-icons.min.css',
      'https://unpkg.com/@tabler/icons-webfont@2.47.0/tabler-icons.min.css');
  }

  private addStylesheet(id: string, href: string, fallback?: string): void {
    if (document.getElementById(id)) { return; }
    var link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = href;
    if (fallback) {
      link.onerror = function () { link.onerror = null; link.href = fallback; };
    }
    document.head.appendChild(link);
  }

  // ─── Scroll reveal (landing page) ────────────────────────────────────

  private initRevealObserver(): void {
    var self = this;
    var elements = this.el.nativeElement.querySelectorAll('.y4c-reveal');
    if (!('IntersectionObserver' in window)) {
      for (var k = 0; k < elements.length; k++) { elements[k].classList.add('y4c-visible'); }
      return;
    }
    this.revealObserver = new IntersectionObserver(function (entries: any[]) {
      entries.forEach(function (entry: any, i: number) {
        if (entry.isIntersecting) {
          setTimeout(function () { entry.target.classList.add('y4c-visible'); }, i * 80);
          self.revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    for (var j = 0; j < elements.length; j++) { this.revealObserver.observe(elements[j]); }
  }

  // ─── Landing page navigation helpers ─────────────────────────────────

  scrollToSubjects(): void {
    const el = document.getElementById('lp-subjects');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }

  // ─── Card helpers ────────────────────────────────────────────────────

  get visibleList(): any[] {
    return this.testList.slice(0, this.visibleCount);
  }

  trackByIndex(i: number): number {
    return i;
  }

  isOnline(t: any): boolean {
    return !!t && t.exam_type === 'Online Session';
  }

  stripFrom(dateStr: string): string {
    return (dateStr || '').replace(/^from\s+/i, '');
  }

  /** "Sun 2 Nov 2025" → { dow: 'Sun', day: '2', mon: 'Nov' }; null if the format doesn't match */
  dateParts(dateStr: string): any {
    var key = dateStr || '';
    if (key in this.datePartsCache) { return this.datePartsCache[key]; }
    var m = key.trim().match(/^([A-Za-z]{3})\w*,?\s+(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]{3})/);
    var res = m ? { dow: m[1], day: m[2], mon: m[3] } : null;
    this.datePartsCache[key] = res;
    return res;
  }

  /** Paper codes in brackets in the test name, e.g. (MN1) (ECOMP01) (CWR01) */
  paperCodes(name: string): string[] {
    var key = name || '';
    if (this.codesCache[key]) { return this.codesCache[key]; }
    var out: string[] = [];
    var re = /\(([A-Z]{1,6}\d{1,3})\)/g;
    var m: any;
    while ((m = re.exec(key)) !== null) {
      if (out.indexOf(m[1]) === -1) { out.push(m[1]); }
    }
    this.codesCache[key] = out;
    return out;
  }

  // ─── Existing calendar methods ───────────────────────────────────────

  private setupUiHandlers() {
    const width = $(window).width();
    if (width < 900) {
      $('.filter-box.filter-tab').eq(0).find('h4').addClass('act');
      $('.filter-box.filter-tab h4').click(function () {
        $('.filter-box h4').removeClass('act');
        $(this).addClass('act');
        $('.filter-box ul').removeClass('act');
        $(this).parent().find('ul').addClass('act');
      });
      $('.open-filter-panel').click(function () { $('#filter-fixed-02').addClass('open'); });
      $('.close-filter-panel').click(function () { $('#filter-fixed-02').removeClass('open'); });
      $('.open-sort-panel').click(function () { $('.sort-panel').addClass('open'); });
      $('.sort-panel .overlay').click(function () { $('.sort-panel').removeClass('open'); });
      $(window).scroll(function () {
        if ($(window).scrollTop() + $(window).height() == $(document).height()) {
          $('.filter-action').hide(); $('.action-btns').hide(); $('.cart-mobile-fixed-btn').hide();
        } else {
          $('.filter-action').show(); $('.action-btns').show(); $('.cart-mobile-fixed-btn').show();
        }
      });
    }

    $('body').on('click', '.tag-ul li', (e: any) => {
      const tagId    = $(e.currentTarget).attr('id');
      const tagClass = $(e.currentTarget).attr('class');
      if (tagClass == 'tag-category') $('[name="category[]"][value="' + tagId + '"]').prop('checked', false);
      if (tagClass == 'tag-course')   $('[name="course[]"][value="' + tagId + '"]').prop('checked', false);
      if (tagClass == 'tag-type')     $('[name="type[]"][value="' + tagId + '"]').prop('checked', false);
      $(e.currentTarget).remove();
      this.applyClientFilters();
    });

    $('body').on('change', '[name="sort"]', (e: any) => {
      const checked = $(e.currentTarget).val();
      $('body').find('[name="release"][value="' + checked + '"]').prop('checked', true);
      this.setFilter();
    });

    $('body').on('keyup', '.filter-search-filter-input', (e: any) => {
      const q = ($(e.currentTarget).val() || '').toString().trim().toUpperCase();
      const ul = $('.test-card-listing-body');
      const li = ul.find('.test-card');
      for (let i = 0; i < li.length; i++) {
        const ftestVal   = li[i].getElementsByClassName('f-test-name')[0].textContent || '';
        const fcatVal    = li[i].getElementsByClassName('f-category-name')[0].textContent || '';
        const fcourseVal = li[i].getElementsByClassName('f-course-name')[0].textContent || '';
        const ftypeVal   = li[i].getElementsByClassName('f-type-name')[0].textContent || '';
        const flocVal    = li[i].getElementsByClassName('f-location-name')[0].textContent || '';
        const ffeeVal    = li[i].getElementsByClassName('f-fees')[0].textContent || '';
        (li[i] as any).style.display = (
          ftestVal.toUpperCase().indexOf(q) > -1 ||
          fcatVal.toUpperCase().indexOf(q)  > -1 ||
          fcourseVal.toUpperCase().indexOf(q) > -1 ||
          ftypeVal.toUpperCase().indexOf(q) > -1 ||
          flocVal.toUpperCase().indexOf(q)  > -1 ||
          ffeeVal.toUpperCase().indexOf(q)  > -1
        ) ? '' : 'none';
      }
    });
  }

  private loadMasterFiltersAndRender() {
    $('.loader-bg').fadeIn();
    this.categoryProduct.getTestFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(res => {
        const data = (res && res.data) ? res.data : {};
        const allCategories = data.category ? data.category : [];
        const allTypes      = data.type ? data.type : [];
        this.courses        = data.course ? data.course : [];
        this.types          = allTypes;
        this.categories     = allCategories.filter((c: any) => c.category_id === 40);

        const allowedPatternNames = [
          'GL', 'CEM', 'CSSE', 'FSCE', 'SET',
          'GL Redbridge', 'GL Kent', 'GLQE Barnet', 'GL Bucks', 'GL Bexley', 'GL HBS',
        ];
        this.patternTypes = allTypes.filter((t: any) => allowedPatternNames.indexOf(t.type) !== -1);
        this.render();
      }, err => {
        console.error('getTestFilters error', err);
        this.render();
      });
  }

  setFilter()    { this.applyClientFilters(); }
  setFilterRes() { this.setFilter(); }

  resetAllFilters() {
    $('.filter-search-filter-input').val('');
    if ($('form[name="filterBox"]').length)    ($('form[name="filterBox"]')[0] as HTMLFormElement).reset();
    if ($('form[name="filterBoxRes"]').length) ($('form[name="filterBoxRes"]')[0] as HTMLFormElement).reset();
    $('[type="checkbox"]').prop('checked', false);
    this.filters = { categoryId: null, pattern: null, mode: null, month: null, center: null, search: '' };
    this.applyClientFilters();
  }

  render() {
    $('.loader-bg').fadeIn();
    const base: any = this.buildBasePayload();
    this.allResults = [];
    const maxPages = 50;
    let pagesFetched = 0;

    const fetchPage = (pageNum: number) => {
      if (pageNum > maxPages) { finalizeLoad(); return; }
      pagesFetched++;
      const formData = { ...base, page: pageNum };
      this.categoryProduct.getTestList(formData)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((res: any) => {
          const chunk = (res && res.data) ? res.data : [];
          if (chunk.length === 0) { finalizeLoad(); return; }
          this.allResults = this.allResults.concat(chunk);
          const extra = res && res.extra && typeof res.extra.extraCount === 'number' ? res.extra.extraCount : null;
          if (extra !== null) {
            extra > 0 ? fetchPage(pageNum + 1) : finalizeLoad();
          } else {
            fetchPage(pageNum + 1);
          }
        }, () => finalizeLoad());
    };

    const finalizeLoad = () => {
      this.centers = this.buildCenters(this.allResults);
      if (this.preselectCategoryId != null) this.filters.categoryId = this.preselectCategoryId;
      this.applyClientFilters();
      if (this.shouldAutoScroll) setTimeout(() => this.scrollToCalendar(), 200);
      $('.loader-bg').fadeOut();
      this.ref.detectChanges();
    };

    fetchPage(1);
  }

  private buildBasePayload() {
    const width = $(window).width();
    let data: any[] = [];
    if (width < 900) {
      $('#filter-fixed-02').removeClass('open');
      if ($('form[name="filterBoxRes"]').length) data = $('form[name="filterBoxRes"]').serializeArray();
    } else {
      if ($('form[name="filterBox"]').length) data = $('form[name="filterBox"]').serializeArray();
    }

    const formData: any = {};
    const course: any[] = []; const category: any[] = []; const type: any[] = [];

    for (let i = 0; i < data.length; i++) {
      if      (data[i]['name'] == 'course[]')   course.push(data[i]['value']);
      else if (data[i]['name'] == 'category[]') category.push(data[i]['value']);
      else if (data[i]['name'] == 'type[]')     type.push(data[i]['value']);
      else formData[data[i]['name']] = data[i]['value'];
    }

    if (this.first && this.catId) category.push(String(this.catId));
    if (!('release'   in formData)) formData['release']   = 'new';
    if (!('exam_type' in formData)) formData['exam_type'] = 'both';
    if (!('search'    in formData)) formData['search']    = '';

    if (this.filters && this.filters.pattern != null) {
      const tid = String(this.filters.pattern);
      if (type.indexOf(tid) === -1) type.push(tid);
    }

    if (this.filters && this.filters.mode) {
      const v = this.filters.mode;
      formData['exam_type'] = (v === 'Online Session') ? 'online'
                            : (v === 'Classroom Session') ? 'classroom'
                            : 'both';
    }

    if (this.filters && this.filters.month  != null) formData['month']    = this.filters.month;
    if (this.filters && this.filters.center)         formData['location'] = this.filters.center;

    formData['course']   = course;
    formData['category'] = category;
    formData['type']     = type;

    const user = +localStorage.getItem('userid');
    formData['user_id'] = (user ? user : 0);
    return formData;
  }

  private isElevenPlus(t: any): boolean {
    if (!t) return false;
    const cid        = Number(t.category_id);
    const courseName = (t.course || '').toString().toLowerCase();
    const courseId   = Number(t.course_id);
    const name       = (t.test_name || '').toString().toLowerCase();
    const ELEVENPLUS_CATEGORY_IDS = [16, 19, 39, 28, 29, 30, 31, 32, 33, 40];
    return (
      courseName === '11plus' || courseId === 1 ||
      ELEVENPLUS_CATEGORY_IDS.indexOf(cid) !== -1 ||
      name.indexOf('11+') !== -1 || name.indexOf('11plus') !== -1
    );
  }

  private monthMap: {[k:string]: number} = { JAN:0,FEB:1,MAR:2,APR:3,MAY:4,JUN:5,JUL:6,AUG:7,SEP:8,OCT:9,NOV:10,DEC:11 };
  private fullMonthMap: {[k:string]: number} = { JANUARY:0,FEBRUARY:1,MARCH:2,APRIL:3,MAY:4,JUNE:5,JULY:6,AUGUST:7,SEPTEMBER:8,OCTOBER:9,NOVEMBER:10,DECEMBER:11 };

  private parseDateString(s?: string): number | null {
    if (!s) return null;
    const str = s.trim();
    let m = str.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
    if (m) { const d=parseInt(m[1],10); const mo=parseInt(m[2],10)-1; let y=parseInt(m[3],10); if(y<100)y+=2000; return Date.UTC(y,mo,d); }
    m = str.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m) { const y=parseInt(m[1],10); const mo=parseInt(m[2],10)-1; const d=parseInt(m[3],10); return Date.UTC(y,mo,d); }
    m = str.match(/(?:from\s+)?(?:mon|tue|wed|thu|fri|sat|sun)?\s*([A-Za-z]+)\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s*(\d{4}))?/i);
    if (m) { const monName=m[1].toUpperCase(); const d=parseInt(m[2],10); const y=m[3]?parseInt(m[3],10):(new Date()).getFullYear(); let monIdx=this.fullMonthMap[monName]; if(monIdx==null)monIdx=this.monthMap[monName.substring(0,3)]; if(monIdx!=null)return Date.UTC(y,monIdx,d); }
    m = str.match(/(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})/);
    if (m) { const d=parseInt(m[1],10); const y=parseInt(m[3],10); const monName=m[2].toUpperCase(); let monIdx=this.fullMonthMap[monName]; if(monIdx==null)monIdx=this.monthMap[monName.substring(0,3)]; if(monIdx!=null)return Date.UTC(y,monIdx,d); }
    return null;
  }

  private getTestTimestamp(t: any): number {
    const td = this.parseDateString((t && t.test_date) || '');
    if (td != null) return td;
    const tn = this.parseDateString((t && t.test_name) || '');
    if (tn != null) return tn;
    return Number.NaN;
  }

  private sortLatestUpcomingFirst(list: any[]): any[] {
    const now = Date.now();
    const future: any[] = []; const past: any[] = []; const unknown: any[] = [];
    for (const item of list) {
      const ts = this.getTestTimestamp(item);
      if (isNaN(ts)) unknown.push(item);
      else if (ts >= now) future.push({ ts, item });
      else past.push({ ts, item });
    }
    future.sort((a,b)=>a.ts-b.ts); past.sort((a,b)=>b.ts-a.ts);
    return future.map(x=>x.item).concat(past.map(x=>x.item)).concat(unknown);
  }

  private applyClientFilters() {
    let filtered = this.allResults.slice().filter(t => this.isElevenPlus(t));

    if (this.filters && this.filters.categoryId != null) {
      const cid = Number(this.filters.categoryId);
      const MOCK_IDS  = [16,28,29,30,31,32,33];
      const TOPIC_IDS = [40];
      const SPRINT_IDS = [39];
      filtered = filtered.filter(t => {
        const tid  = Number(t.category_id);
        const name = ((t.test_name || '')+'').toLowerCase();
        const cat  = ((t.category  || '')+'').toLowerCase();
        if (cid===16)  return MOCK_IDS.includes(tid)   || name.includes('mock')   || cat.includes('mock');
        if (cid===40)  return TOPIC_IDS.includes(tid)  || name.includes('topic')  || cat.includes('topic');
        if (cid===39)  return SPRINT_IDS.includes(tid) || name.includes('sprint') || cat.includes('sprint');
        return tid === cid;
      });
    }

    if (this.filters && this.filters.pattern != null) {
      const patternMeta  = this.patternTypes.find(p => String(p.type_id) === String(this.filters.pattern));
      const patternLabel = patternMeta ? (patternMeta.type||'').toLowerCase() : '';
      if (patternLabel) {
        filtered = filtered.filter(t => {
          const tType = ((t.type||'')+'').toLowerCase();
          const tName = ((t.test_name||'')+'').toLowerCase();
          return tType.includes(patternLabel) || tName.includes(patternLabel);
        });
      }
    }

    if (this.filters && this.filters.mode && this.filters.mode !== 'Both') {
      const modeVal = (this.filters.mode||'').toLowerCase();
      filtered = filtered.filter(t => ((t.exam_type||'')+'').toLowerCase().includes(modeVal));
    }

    if (this.filters && this.filters.month) {
      filtered = filtered.filter(t => this.itemInMonth(t, Number(this.filters.month)));
    }

    if (this.filters && this.filters.center && this.filters.center !== 'Any') {
      const centerName = (this.filters.center||'').toLowerCase();
      filtered = filtered.filter(t => ((t.all_location||t.location||'')+'').toLowerCase().includes(centerName));
    }

    if (this.filters && this.filters.search) {
      const q = (this.filters.search+'').toLowerCase().trim();
      if (q) {
        filtered = filtered.filter(t => {
          const name   = ((t.test_name||'')+'').toLowerCase();
          const loc    = ((t.all_location||t.location||'')+'').toLowerCase();
          const course = ((t.course||'')+'').toLowerCase();
          const cat    = ((t.category||'')+'').toLowerCase();
          const type   = ((t.type||'')+'').toLowerCase();
          return name.includes(q)||loc.includes(q)||course.includes(q)||cat.includes(q)||type.includes(q);
        });
      }
    }

    this.testList = this.sortLatestUpcomingFirst(filtered);
    this.visibleCount = PAGE_SIZE;
    this.centers  = this.buildCenters(this.allResults);
    $('.loader-bg').fadeOut();
    this.ref.detectChanges();
  }

  loadMore(append: boolean) {
    this.visibleCount += PAGE_SIZE;
    this.ref.detectChanges();
  }

  private itemMatchesPattern(t: any, tid: string): boolean {
    const meta  = this.patternTypes.find(x => String(x.type_id) === tid);
    if (!meta) return true;
    const label = (meta.type||'').toString().toUpperCase();
    const name  = ((t && t.test_name)||'').toString().toUpperCase();
    const ALIASES: { [k: string]: RegExp[] } = {
      'GL': [/\bGL\b/,/\bGL[-_]/,/\bGL[A-Z]{2}\d*/], 'CEM': [/\bCEM\b/],
      'CSSE': [/\bCSSE\b/], 'FSCE': [/\bFSCE\b/], 'SET': [/\bSET\b/],
      'GL REDBRIDGE': [/\bREDBRIDGE\b/,/\bGLRB\d*\b/], 'GL BEXLEY': [/\bBEXLEY\b/,/\bGLBX\d*\b/],
      'GL KENT': [/\bKENT\b/,/\bGLKE\d*\b/], 'GL BUCKS': [/\bBUCKS\b/,/\bGLBU\d*\b/],
      'GL QE BARNET': [/\bQE\b/,/\bQE\s*BARNET\b/,/\bGLQE\d*\b/], 'GL HBS': [/\bHBS\b/,/\bGLHBS\d*\b/],
    };
    const patterns = ALIASES[label];
    if (patterns && patterns.length) return patterns.some(rx => rx.test(name));
    return name.indexOf(label) !== -1 || name.replace(/\s+/g,'').indexOf(label.replace(/\s+/g,'')) !== -1;
  }

  private itemInMonth(t: any, monthNum: number): boolean {
    const s = ((t && t.test_date)||'').toString().toUpperCase();
    if (!s) return true;
    const monthsShort = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
    const monthsLong  = ['JANUARY','FEBRUARY','MARCH','APRIL','MAY','JUNE','JULY','AUGUST','SEPTEMBER','OCTOBER','NOVEMBER','DECEMBER'];
    const idx = monthNum - 1;
    const mm  = (monthNum < 10 ? '0' + monthNum : '' + monthNum);
    return (
      s.indexOf(monthsShort[idx]) !== -1 || s.indexOf(monthsLong[idx]) !== -1 ||
      new RegExp('\\b\\d{4}-' + mm + '-\\d{1,2}\\b').test(s) ||
      new RegExp('\\b\\d{1,2}/' + mm + '/\\d{2,4}\\b').test(s)
    );
  }

  private buildCenters(list: any[]): string[] {
    const set: any = {};
    for (let i = 0; i < (list||[]).length; i++) {
      const v = ((list[i].all_location || list[i].location || '')+'').trim();
      if (v) set[v] = true;
    }
    return Object.keys(set).sort();
  }

  trimChar(str: string, length = 30) {
    if (!str) return '';
    return str.length < length ? str : str.substring(0, length) + '...';
  }

  isRangeDate(dateStr: string): boolean {
    if (!dateStr) return false;
    return /^(from\s+)/i.test(dateStr.trim());
  }

  private locationSpecificList(val?: any): void {
    let filter = (val || '').toUpperCase();
    const ul = $('.test-card-listing-body');
    const li = ul.find('.test-card');
    for (let i = 0; i < li.length; i++) {
      const ftestVal   = li[i].getElementsByClassName('f-test-name')[0].textContent || '';
      const fcatVal    = li[i].getElementsByClassName('f-category-name')[0].textContent || '';
      const fcourseVal = li[i].getElementsByClassName('f-course-name')[0].textContent || '';
      const ftypeVal   = li[i].getElementsByClassName('f-type-name')[0].textContent || '';
      const flocVal    = li[i].getElementsByClassName('f-location-name')[0].textContent || '';
      const ffeeVal    = li[i].getElementsByClassName('f-fees')[0].textContent || '';
      (li[i] as any).style.display = (
        ftestVal.toUpperCase().indexOf(filter)   > -1 ||
        fcatVal.toUpperCase().indexOf(filter)    > -1 ||
        fcourseVal.toUpperCase().indexOf(filter) > -1 ||
        ftypeVal.toUpperCase().indexOf(filter)   > -1 ||
        flocVal.toUpperCase().indexOf(filter)    > -1 ||
        ffeeVal.toUpperCase().indexOf(filter)    > -1
      ) ? '' : 'none';
    }
  }

  scrollToCalendar() {
    const calendarElement = document.getElementById('calendar-section');
    if (calendarElement) calendarElement.scrollIntoView({ behavior: 'smooth' });
  }

  scrrollToOffer() {
    const offerElement = document.getElementById('offer-section');
    if (offerElement) offerElement.scrollIntoView({ behavior: 'smooth' });
  }

  downloadCalendar() {
    window.location.href = 'https://drive.google.com/uc?export=download&id=1iOO5PXJtTqgihbsgo1lRVciN7xufo4kO';
  }

  downloadsprint() {
    window.location.href = 'https://drive.google.com/uc?export=download&id=1at2OesLa2qU5T6HPkJIm0bvhbnSgpNZv';
  }
}
